import { useMemo } from 'react';
import { LocationFeatureSettings } from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import { useQuery } from 'react-query';
import { PaymentsView1 } from '@frontend/api-payments-view';
import { SchemaPaymentsViewsService } from '@frontend/schema';
import { useSelectedLocations } from '../../providers/selected-location-context';
import {
  paymentsLocationFeatureSettings,
  PaymentsFeatureSettings,
  PaymentFeatureSettingsValues,
  paymentFeatureSettingsWithValues,
  PaymentFeatureSettingsValuesMap,
} from '../../types/payment-hooks.types';
import { MixedBoolean } from '../../utils/mixed-values';
import { getInterScreenQueryOptions } from '../../utils/query-helpers';

export type FeatureSettingData = LocationFeatureSettings & {
  enabled: boolean;
};

export const getLocationFeatureSettingsQueryKey = (locationId: string, settingName: PaymentsFeatureSettings) => [
  locationId,
  'feature-flags',
  settingName,
];

interface UseLocationFeatureSettingsOptions {
  locationIds?: string[];
  locationId?: string;
}

/**
 * Not safe to use everywhere, because it uses selected locations context.
 * Use with caution.
 */
export const useLocationFeatureSettings = (
  settingName: PaymentsFeatureSettings,
  { locationIds: locationIdsParam, locationId }: UseLocationFeatureSettingsOptions = {}
) => {
  const { locationIds: locationIdsFromUtils } = useSelectedLocations();
  const locationIds = (locationIdsParam ?? locationIdsFromUtils).filter(Boolean) as string[];

  return useLocationFeatureSettingsDirect(settingName, { locationIds, locationId });
};

/**
 * Prop driven. Use when you can supply the location IDs that should be used.
 */
export const useLocationFeatureSettingsDirect = (
  settingName: PaymentsFeatureSettings,
  { locationIds: locationIdsParam, locationId }: UseLocationFeatureSettingsOptions = {}
) => {
  const uniqueLocationIds = useMemo(() => {
    return Array.from(new Set(locationIdsParam ?? []));
  }, [locationIdsParam]);

  const { data, ...queryData } = PaymentsView1.Queries.useGetMultiLocationFeatureSettingsQuery(
    {
      locationIds: uniqueLocationIds,
    },
    {
      ...getInterScreenQueryOptions(),
      enabled: uniqueLocationIds.length > 0,
    }
  );

  const featureSettingsResults = useMemo(() => {
    return data?.settings ?? [];
  }, [data]);

  const { featureSettingsData, isAllEnabled, isAnyEnabled, enabledLocations } = useMemo(() => {
    let isAllEnabled = featureSettingsResults.length > 0;
    let isAnyEnabled = false;
    const featureSettingsData: Record<string, FeatureSettingData> = {};
    const enabledLocations: string[] = [];

    featureSettingsResults.forEach((featureSettingResult) => {
      const locationId = featureSettingResult.locationId || '';

      const settingKey = paymentsLocationFeatureSettings[settingName];
      let enabled = false;
      if (settingKey) {
        enabled = !!((settingKey && featureSettingResult[settingKey]) ?? false);
      } else {
        console.error(`Setting key for ${settingName} not found in paymentsLocationFeatureSettings.`);
      }
      isAllEnabled = isAllEnabled && enabled;
      isAnyEnabled = isAnyEnabled || enabled;
      if (enabled) {
        enabledLocations.push(locationId);
      }
      featureSettingsData[locationId] = { ...featureSettingResult, enabled };
    });

    return { featureSettingsData, isAllEnabled, isAnyEnabled, enabledLocations };
  }, [featureSettingsResults]);

  const refetchAll = () => queryData.refetch();

  const firstLocationId = uniqueLocationIds[0] ?? '';

  return {
    ...queryData,
    featureSettingsData,
    ...featureSettingsData[firstLocationId],
    isAllEnabled,
    isAnyEnabled,
    mixedValue: (isAnyEnabled && !isAllEnabled ? 'mixed' : isAllEnabled) as MixedBoolean,
    ...(locationId ? { specifiedLocationEnabled: featureSettingsData[locationId]?.enabled } : {}),
    refetchAll,
    enabledLocations,
  };
};

export const useLocationFeatureSetting = (settingName: PaymentsFeatureSettings, locationId: string) => {
  const queryKey = getLocationFeatureSettingsQueryKey(locationId, settingName);

  const { data, ...queryResult } = useQuery({
    queryKey,

    queryFn: () => SchemaPaymentsViewsService.LocationFeatureSettings({ locationId }),

    enabled: !!locationId,

    ...getInterScreenQueryOptions(),
  });

  const settingKey = paymentsLocationFeatureSettings[settingName];

  let enabled = false;

  if (settingKey) {
    enabled = !!((settingKey && data?.settings?.[settingKey]) ?? false);
  } else {
    console.error(`Setting key for ${settingName} not found in paymentsLocationFeatureSettings.`);
  }

  return { enabled, ...queryResult, queryKey, isLoading: queryResult.isLoading };
};

/**
 * Use a traditional settingName to retrieve a coresponding, non-boolean value from the same object.
 * Not a great BE response pattern, but we go with the flow.
 */
export const useLocationFeatureSettingValue = <T extends PaymentFeatureSettingsValues>(
  settingName: T,
  { locationIds: locationIdsParam }: UseLocationFeatureSettingsOptions = {}
) => {
  const locationIds = (locationIdsParam ?? []).filter(Boolean) as string[];

  const uniqueLocationIds = useMemo(() => {
    return Array.from(new Set(locationIds));
  }, [locationIds]);

  const { data, isLoading } = PaymentsView1.Queries.useGetMultiLocationFeatureSettingsQuery(
    {
      locationIds: uniqueLocationIds,
    },
    {
      ...getInterScreenQueryOptions(),
      enabled: uniqueLocationIds.length > 0,
    }
  );

  const featureSettingsResults = useMemo(() => {
    return data?.settings ?? [];
  }, [data]);

  const settingValueKey = paymentFeatureSettingsWithValues[settingName];

  const values = featureSettingsResults.reduce(
    (acc, locationSettings) => {
      const locationId = locationSettings.locationId || '';

      acc[locationId] = { value: locationSettings[settingValueKey], isLoading };

      return acc;
    },
    {} as Record<
      string,
      {
        value: PaymentFeatureSettingsValuesMap<T>;
        isLoading: boolean;
      }
    >
  );

  const valuesSafe = useMemo(() => {
    return values;
  }, [JSON.stringify(values)]);

  return valuesSafe;
};
